import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { inspectionAPI, vehicleAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
    GitCompare, Truck, Calendar, Loader2, ChevronLeft, ChevronRight,
    ArrowLeftRight, ZoomIn, AlertTriangle, Check, Brain, Sparkles,
    AlertCircle, CheckCircle, XCircle, Info, LogIn, LogOut
} from 'lucide-react';
import { toast } from 'sonner';
import { LatestDriverPhotos } from '../components/inspections/LatestDriverPhotos';

const PHOTO_TYPES = [
    { type: 'front', label: 'Front View', labelEl: 'Μπροστά' },
    { type: 'back', label: 'Rear View', labelEl: 'Πίσω' },
    { type: 'left', label: 'Left Side', labelEl: 'Αριστερά' },
    { type: 'right', label: 'Right Side', labelEl: 'Δεξιά' },
    { type: 'interior_front', label: 'Interior Front', labelEl: 'Εσωτερικό Μπροστά' },
    { type: 'interior_back', label: 'Interior Back', labelEl: 'Εσωτερικό Πίσω' }
];

const LEGACY_TYPES = { interior_front: 'dashboard', interior_back: 'cargo' };

const hasSourcePhoto = (inspection, source, photoType) => {
    const accepted = [photoType, LEGACY_TYPES[photoType]].filter(Boolean);
    if (source === 'checkout') {
        return (inspection?.checkout_photos || []).some(p => accepted.includes(p.type));
    }
    return (inspection?.photos || []).some(p => accepted.includes(p.photo_type));
};

const ComparisonPhoto = ({ inspection, photoType, label, source = 'checkin' }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (inspection) {
            loadPhoto();
        } else {
            setImageUrl(null);
        }
    }, [inspection, photoType, source]);

    const loadPhoto = async () => {
        if (!hasSourcePhoto(inspection, source, photoType)) {
            setImageUrl(null);
            return;
        }

        setLoading(true);
        setError(false);
        try {
            const url = source === 'checkout'
                ? inspectionAPI.getCheckoutPhotoUrl(inspection.id, photoType)
                : inspectionAPI.getPhotoUrl(inspection.id, photoType);
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('fleet_token')}`
                }
            });
            if (!response.ok) throw new Error('Failed to load');
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setImageUrl(objectUrl);
        } catch (err) {
            console.error(`Failed to load ${photoType}:`, err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-2">
            <div className="aspect-video bg-zinc-800 rounded-sm overflow-hidden relative">
                {loading ? (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-zinc-500 animate-spin" />
                    </div>
                ) : imageUrl ? (
                    <img 
                        src={imageUrl} 
                        alt={label}
                        className="w-full h-full object-cover cursor-zoom-in hover:opacity-90 transition-opacity"
                        onClick={() => window.open(imageUrl, '_blank')}
                    />
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center text-zinc-600">
                        <span className="text-xs">No photo</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default function ComparisonPage() {
    const { canManage } = useAuth();
    const { language } = useLanguage();
    const el = language === 'el';
    const [vehicles, setVehicles] = useState([]);
    const [inspections, setInspections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [vehicleInspections, setVehicleInspections] = useState([]);
    const [leftInspection, setLeftInspection] = useState(null);
    const [rightInspection, setRightInspection] = useState(null);
    const [leftSource, setLeftSource] = useState('checkin');
    const [rightSource, setRightSource] = useState('checkout');
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [viewMode, setViewMode] = useState('grid'); // grid or single
    const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
    
    // AI Analysis State
    const [aiAnalyzing, setAiAnalyzing] = useState(false);
    const [aiResults, setAiResults] = useState(null);
    const [analyzingPhotoType, setAnalyzingPhotoType] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (selectedVehicle) {
            const filtered = inspections
                .filter(i => i.vehicle_id === selectedVehicle)
                .sort((a, b) => new Date(b.date) - new Date(a.date));
            setVehicleInspections(filtered);
            
            // Auto-select the two most recent inspections
            if (filtered.length >= 2) {
                setLeftInspection(filtered[1]); // Older
                setRightInspection(filtered[0]); // Newer
            } else if (filtered.length === 1) {
                setLeftInspection(filtered[0]);
                setRightInspection(filtered[0]);
            } else {
                setLeftInspection(null);
                setRightInspection(null);
            }
        }
    }, [selectedVehicle, inspections]);

    const fetchData = async () => {
        try {
            const [vehiclesRes, inspectionsRes] = await Promise.all([
                vehicleAPI.getAll(),
                inspectionAPI.getAll()
            ]);
            setVehicles(vehiclesRes.data);
            setInspections(inspectionsRes.data);
            const mostRecent = [...inspectionsRes.data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
            if (mostRecent) setSelectedVehicle(mostRecent.vehicle_id);
        } catch (error) {
            toast.error('Failed to fetch data');
        } finally {
            setLoading(false);
        }
    };

    const getInspectionById = (id) => {
        return inspections.find(i => i.id === id);
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const swapInspections = () => {
        const tempInspection = leftInspection;
        const tempSource = leftSource;
        setLeftInspection(rightInspection);
        setLeftSource(rightSource);
        setRightInspection(tempInspection);
        setRightSource(tempSource);
        setAiResults(null);
    };

    const sourceLabel = (source) => (source === 'checkout'
        ? (el ? 'Παράδοση (Check-out)' : 'Check-out')
        : (el ? 'Παραλαβή (Check-in)' : 'Check-in'));

    const photoCount = (inspection, source) => (source === 'checkout'
        ? (inspection?.checkout_photos?.length || 0)
        : (inspection?.photos?.length || 0));

    // AI Analysis Functions
    const runAiAnalysis = async (photoType = null) => {
        if (!leftInspection || !rightInspection) {
            toast.error('Select both inspections first');
            return;
        }

        setAiAnalyzing(true);
        setAnalyzingPhotoType(photoType);

        try {
            const response = await inspectionAPI.aiCompare({
                before_inspection_id: leftInspection.id,
                after_inspection_id: rightInspection.id,
                before_source: leftSource,
                after_source: rightSource,
                photo_type: photoType || null
            });

            const resultsObj = {};
            response.data.results?.forEach(r => { resultsObj[r.photo_type] = r; });
            setAiResults(prev => (photoType ? { ...prev, ...resultsObj } : resultsObj));

            const invalid = (response.data.results || []).some(r => r.status === 'invalid_photos');
            if (response.data.overall_has_damage) {
                toast.warning(el ? `Εντοπίστηκε ζημιά! Σοβαρότητα: ${response.data.overall_severity}` : `Damage detected! Severity: ${response.data.overall_severity}`);
            } else if (invalid) {
                toast.warning(el ? 'Κάποιες φωτογραφίες δεν αντιστοιχούν μεταξύ τους' : 'Some photos do not match each other');
            } else {
                toast.success(el ? 'Δεν εντοπίστηκε ζημιά' : 'No damage detected');
            }
        } catch (error) {
            console.error('AI analysis failed:', error);
            toast.error(error.response?.data?.detail || 'AI analysis failed');
        } finally {
            setAiAnalyzing(false);
            setAnalyzingPhotoType(null);
        }
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'high': return 'text-red-500 bg-red-50';
            case 'medium': return 'text-orange-500 bg-orange-50';
            case 'low': return 'text-amber-600 bg-amber-50';
            default: return 'text-green-500 bg-green-500/10';
        }
    };

    const getSeverityIcon = (severity) => {
        switch (severity) {
            case 'high': return <XCircle className="w-5 h-5" />;
            case 'medium': return <AlertCircle className="w-5 h-5" />;
            case 'low': return <AlertTriangle className="w-5 h-5" />;
            default: return <CheckCircle className="w-5 h-5" />;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="comparison-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <GitCompare className="w-6 h-6 text-[#1B7EB5]" />
                        {el ? 'Σύγκριση Φωτογραφιών' : 'Photo Comparison'}
                    </h1>
                    <p className="text-zinc-500">{el ? 'Συγκρίνετε τους ελέγχους οχημάτων μεταξύ διαφορετικών ημερών' : 'Compare vehicle inspections across different days'}</p>
                </div>
            </div>

            {/* Vehicle Selection */}
            <div className="fleet-card p-4">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-end">
                    <div className="flex-1 w-full md:w-auto">
                        <label className="text-xs text-zinc-500 uppercase tracking-wider mb-2 block">{el ? 'Επιλογή Οχήματος' : 'Select Vehicle'}</label>
                        <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                            <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="comparison-vehicle-select">
                                <Truck className="w-4 h-4 mr-2 text-zinc-500" />
                                <SelectValue placeholder={el ? 'Διαλέξτε όχημα' : 'Choose a vehicle to compare'} />
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                                {vehicles.map(vehicle => (
                                    <SelectItem key={vehicle.id} value={vehicle.id}>
                                        {vehicle.make} {vehicle.model} ({vehicle.license_plate})
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {selectedVehicle && vehicleInspections.length >= 1 && (
                        <div className="flex gap-2">
                            <Button
                                variant={viewMode === 'grid' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setViewMode('grid')}
                                className={viewMode === 'grid' ? 'bg-[#1B7EB5] hover:bg-[#58A6B4] text-white' : 'border-zinc-700 text-zinc-400'}
                            >
                                {el ? 'Πλέγμα' : 'Grid View'}
                            </Button>
                            <Button
                                variant={viewMode === 'single' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setViewMode('single')}
                                className={viewMode === 'single' ? 'bg-[#1B7EB5] hover:bg-[#58A6B4] text-white' : 'border-zinc-700 text-zinc-400'}
                            >
                                {el ? 'Μία-μία' : 'Single View'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Latest photos uploaded by the driver */}
            {selectedVehicle && vehicleInspections.length > 0 && (
                <LatestDriverPhotos inspection={vehicleInspections[0]} />
            )}

            {/* No Vehicle Selected */}
            {!selectedVehicle && (
                <div className="fleet-card p-12 text-center">
                    <GitCompare className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-800 mb-2">{el ? 'Επιλέξτε όχημα' : 'Select a Vehicle'}</h3>
                    <p className="text-zinc-500">{el ? 'Διαλέξτε όχημα πιο πάνω για να δείτε και να συγκρίνετε τις φωτογραφίες των ελέγχων' : 'Choose a vehicle above to compare its inspection photos over time'}</p>
                </div>
            )}

{/* Comparison View */}
            {selectedVehicle && vehicleInspections.length >= 1 && (
                <>
                    {/* Date Selectors */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Left (Older) Inspection */}
                        <div className="fleet-card p-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs text-zinc-500 uppercase tracking-wider">{el ? 'Πριν (παλαιότερος)' : 'Before (Older)'}</span>
                                <span className="text-xs text-red-400">{el ? 'Αναφορά' : 'Reference'}</span>
                            </div>
                            <Select 
                                value={leftInspection?.id || ''} 
                                onValueChange={(id) => setLeftInspection(getInspectionById(id))}
                            >
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="left-inspection-select">
                                    <Calendar className="w-4 h-4 mr-2 text-zinc-500" />
                                    <SelectValue placeholder={el ? 'Επιλέξτε ημερομηνία' : 'Select date'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    {vehicleInspections.map(inspection => (
                                        <SelectItem 
                                            key={inspection.id} 
                                            value={inspection.id}
                                        >
                                            {formatDate(inspection.date)} - {inspection.driver_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={leftSource} onValueChange={(v) => { setLeftSource(v); setAiResults(null); }}>
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-2" data-testid="left-source-select">
                                    <LogIn className="w-4 h-4 mr-2 text-zinc-500" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="checkin">{el ? 'Παραλαβή (Check-in)' : 'Check-in'}</SelectItem>
                                    <SelectItem value="checkout">{el ? 'Παράδοση (Check-out)' : 'Check-out'}</SelectItem>
                                </SelectContent>
                            </Select>
                            {leftInspection && (
                                <div className="mt-2 text-sm text-zinc-400">
                                    <span className="text-[#1B7EB5]">{leftInspection.odometer_reading?.toLocaleString()} km</span>
                                    <span className="mx-2">•</span>
                                    <span>{photoCount(leftInspection, leftSource)}/6 {el ? 'φωτογραφίες' : 'photos'}</span>
                                    <span className="mx-2">•</span>
                                    <span className="text-zinc-500">{sourceLabel(leftSource)}</span>
                                </div>
                            )}
                        </div>

                        {/* Swap Button */}
                        <div className="hidden md:flex items-center justify-center absolute left-1/2 -translate-x-1/2 z-10" style={{ top: '50%' }}>
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={swapInspections}
                                className="rounded-full border-zinc-700 bg-zinc-900 hover:bg-zinc-800"
                                title="Swap dates"
                            >
                                <ArrowLeftRight className="w-4 h-4" />
                            </Button>
                        </div>

                        {/* Right (Newer) Inspection */}
                        <div className="fleet-card p-4">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-xs text-zinc-500 uppercase tracking-wider">{el ? 'Μετά (νεότερος)' : 'After (Newer)'}</span>
                                <span className="text-xs text-green-400">{el ? 'Τρέχων' : 'Current'}</span>
                            </div>
                            <Select 
                                value={rightInspection?.id || ''} 
                                onValueChange={(id) => setRightInspection(getInspectionById(id))}
                            >
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900" data-testid="right-inspection-select">
                                    <Calendar className="w-4 h-4 mr-2 text-zinc-500" />
                                    <SelectValue placeholder={el ? 'Επιλέξτε ημερομηνία' : 'Select date'} />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    {vehicleInspections.map(inspection => (
                                        <SelectItem 
                                            key={inspection.id} 
                                            value={inspection.id}
                                        >
                                            {formatDate(inspection.date)} - {inspection.driver_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Select value={rightSource} onValueChange={(v) => { setRightSource(v); setAiResults(null); }}>
                                <SelectTrigger className="bg-white border-slate-300 text-slate-900 mt-2" data-testid="right-source-select">
                                    <LogOut className="w-4 h-4 mr-2 text-zinc-500" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-white border-slate-200">
                                    <SelectItem value="checkin">{el ? 'Παραλαβή (Check-in)' : 'Check-in'}</SelectItem>
                                    <SelectItem value="checkout">{el ? 'Παράδοση (Check-out)' : 'Check-out'}</SelectItem>
                                </SelectContent>
                            </Select>
                            {rightInspection && (
                                <div className="mt-2 text-sm text-zinc-400">
                                    <span className="text-[#1B7EB5]">{rightInspection.odometer_reading?.toLocaleString()} km</span>
                                    <span className="mx-2">•</span>
                                    <span>{photoCount(rightInspection, rightSource)}/6 {el ? 'φωτογραφίες' : 'photos'}</span>
                                    <span className="mx-2">•</span>
                                    <span className="text-zinc-500">{sourceLabel(rightSource)}</span>
                                    {leftInspection && (
                                        <>
                                            <span className="mx-2">•</span>
                                            <span className="text-green-400">
                                                +{(rightInspection.odometer_reading - leftInspection.odometer_reading).toLocaleString()} km
                                            </span>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile Swap Button */}
                    <div className="flex md:hidden justify-center -my-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={swapInspections}
                            className="border-zinc-700"
                        >
                            <ArrowLeftRight className="w-4 h-4 mr-2" />
                            {el ? 'Αντιστροφή' : 'Swap'}
                        </Button>
                    </div>

                    {/* AI Analysis Button */}
                    {leftInspection && rightInspection && (
                        <div className="fleet-card p-4 border-purple-500/30 bg-purple-500/5">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-purple-500/10 rounded-full flex items-center justify-center">
                                        <Brain className="w-5 h-5 text-purple-500" />
                                    </div>
                                    <div>
                                        <h3 className="text-slate-800 font-medium flex items-center gap-2">
                                            {el ? 'Έλεγχος ζημιών με AI' : 'AI Damage Detection'}
                                            <Sparkles className="w-4 h-4 text-purple-400" />
                                        </h3>
                                        <p className="text-sm text-zinc-500">Powered by GPT-4o Vision</p>
                                    </div>
                                </div>
                                <Button
                                    onClick={() => runAiAnalysis()}
                                    disabled={aiAnalyzing}
                                    className="bg-purple-600 hover:bg-purple-500 text-white"
                                    data-testid="ai-analyze-btn"
                                >
                                    {aiAnalyzing ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            {el ? 'Γίνεται ανάλυση...' : 'Analyzing...'}
                                        </>
                                    ) : (
                                        <>
                                            <Brain className="w-4 h-4 mr-2" />
                                            {el ? 'Ανάλυση όλων των φωτογραφιών' : 'Analyze All Photos'}
                                        </>
                                    )}
                                </Button>
                            </div>

                            {/* AI Results Summary */}
                            {aiResults && Object.keys(aiResults).length > 0 && (
                                <div className="mt-4 pt-4 border-t border-purple-500/20">
                                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                                        {PHOTO_TYPES.map(({ type, label, labelEl }) => {
                                            const result = aiResults[type];
                                            if (!result) return (
                                                <div key={type} className="bg-slate-100 rounded-sm p-3 text-center">
                                                    <p className="text-xs text-zinc-500 mb-1">{el ? labelEl : label}</p>
                                                    <Info className="w-4 h-4 text-zinc-600 mx-auto" />
                                                    <p className="text-xs text-zinc-600 mt-1">No data</p>
                                                </div>
                                            );
                                            return (
                                                <div 
                                                    key={type} 
                                                    className={`rounded-sm p-3 text-center ${result.status === 'invalid_photos' ? 'bg-amber-50 text-amber-700' : getSeverityColor(result.severity)}`}
                                                >
                                                    <p className="text-xs opacity-70 mb-1">{el ? labelEl : label}</p>
                                                    <div className="flex justify-center">
                                                        {result.status === 'invalid_photos'
                                                            ? <AlertCircle className="w-4 h-4" />
                                                            : getSeverityIcon(result.severity)}
                                                    </div>
                                                    <p className="text-xs font-medium mt-1 capitalize">
                                                        {result.status === 'invalid_photos'
                                                            ? (el ? 'ασύμβατες' : 'mismatch')
                                                            : result.status === 'missing_photos'
                                                                ? (el ? 'λείπουν' : 'missing')
                                                                : (result.severity || 'none')}
                                                    </p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Grid View */}
                    {viewMode === 'grid' && leftInspection && rightInspection && (
                        <div className="space-y-6">
                            {PHOTO_TYPES.map(({ type, label, labelEl }) => {
                                const aiResult = aiResults?.[type];
                                return (
                                <div key={type} className="fleet-card p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
                                            <span className="w-2 h-2 bg-[#1B7EB5] rounded-full"></span>
                                            {el ? labelEl : label}
                                        </h3>
                                        {/* Individual AI Analysis Button */}
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => runAiAnalysis(type)}
                                            disabled={aiAnalyzing}
                                            className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                        >
                                            {analyzingPhotoType === type ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <>
                                                    <Brain className="w-4 h-4 mr-1" />
                                                    Analyze
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-xs text-red-400 mb-2">{formatDate(leftInspection.date)} · {sourceLabel(leftSource)}</p>
                                            <ComparisonPhoto 
                                                inspection={leftInspection} 
                                                photoType={type} 
                                                label={`${label} - Before`} 
                                                source={leftSource}
                                            />
                                        </div>
                                        <div>
                                            <p className="text-xs text-green-400 mb-2">{formatDate(rightInspection.date)} · {sourceLabel(rightSource)}</p>
                                            <ComparisonPhoto 
                                                inspection={rightInspection} 
                                                photoType={type} 
                                                label={`${label} - After`} 
                                                source={rightSource}
                                            />
                                        </div>
                                    </div>

                                    {/* AI Analysis Results for this photo */}
                                    {aiResult && (
                                        <div className={`mt-4 p-3 rounded-sm ${aiResult.status === 'invalid_photos' ? 'bg-amber-50 text-amber-700' : getSeverityColor(aiResult.severity)}`}>
                                            <div className="flex items-start gap-3">
                                                {aiResult.status === 'invalid_photos'
                                                    ? <AlertCircle className="w-5 h-5" />
                                                    : getSeverityIcon(aiResult.severity)}
                                                <div className="flex-1">
                                                    <p className="font-medium">
                                                        {aiResult.status === 'missing_photos'
                                                            ? (el ? 'Λείπουν φωτογραφίες για σύγκριση' : 'Photos missing for this view')
                                                            : aiResult.status === 'failed'
                                                                ? (el ? 'Ο έλεγχος απέτυχε' : 'Comparison failed')
                                                                : aiResult.status === 'invalid_photos'
                                                                    ? (el ? 'Μη έγκυρες / ασύμβατες φωτογραφίες' : 'Invalid or mismatched photos')
                                                                    : aiResult.damage_detected
                                                                        ? (el ? `Εντοπίστηκε ζημιά (${aiResult.severity})` : `${aiResult.severity} damage detected`)
                                                                        : (el ? 'Δεν εντοπίστηκε ζημιά' : 'No damage detected')}
                                                    </p>
                                                    {(el ? aiResult.description_el : aiResult.description_en) && (
                                                        <p className="mt-1 text-sm opacity-80">
                                                            {el ? aiResult.description_el : aiResult.description_en}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )})}
                        </div>
                    )}

                    {/* Single View (Carousel) */}
                    {viewMode === 'single' && leftInspection && rightInspection && (
                        <div className="fleet-card p-6">
                            <div className="flex items-center justify-between mb-4">
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setCurrentPhotoIndex(Math.max(0, currentPhotoIndex - 1))}
                                    disabled={currentPhotoIndex === 0}
                                    className="border-zinc-700"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-slate-800">
                                        {el ? PHOTO_TYPES[currentPhotoIndex].labelEl : PHOTO_TYPES[currentPhotoIndex].label}
                                    </h3>
                                    <p className="text-sm text-zinc-500">
                                        {currentPhotoIndex + 1} of {PHOTO_TYPES.length}
                                    </p>
                                </div>

                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => setCurrentPhotoIndex(Math.min(PHOTO_TYPES.length - 1, currentPhotoIndex + 1))}
                                    disabled={currentPhotoIndex === PHOTO_TYPES.length - 1}
                                    className="border-zinc-700"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>

                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm text-red-400 font-medium">{el ? 'Πριν' : 'Before'}</p>
                                        <p className="text-xs text-zinc-500">{formatDate(leftInspection.date)} · {sourceLabel(leftSource)}</p>
                                    </div>
                                    <div className="aspect-video">
                                        <ComparisonPhoto 
                                            inspection={leftInspection} 
                                            photoType={PHOTO_TYPES[currentPhotoIndex].type} 
                                            label="Before" 
                                            source={leftSource}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-sm text-green-400 font-medium">{el ? 'Μετά' : 'After'}</p>
                                        <p className="text-xs text-zinc-500">{formatDate(rightInspection.date)} · {sourceLabel(rightSource)}</p>
                                    </div>
                                    <div className="aspect-video">
                                        <ComparisonPhoto 
                                            inspection={rightInspection} 
                                            photoType={PHOTO_TYPES[currentPhotoIndex].type} 
                                            label="After" 
                                            source={rightSource}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Thumbnail Navigation */}
                            <div className="flex justify-center gap-2 mt-6">
                                {PHOTO_TYPES.map((photo, index) => (
                                    <button
                                        key={photo.type}
                                        onClick={() => setCurrentPhotoIndex(index)}
                                        className={`w-3 h-3 rounded-full transition-colors ${
                                            index === currentPhotoIndex 
                                                ? 'bg-[#1B7EB5]' 
                                                : 'bg-zinc-700 hover:bg-zinc-600'
                                        }`}
                                        title={el ? photo.labelEl : photo.label}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Summary Stats */}
                    {leftInspection && rightInspection && (
                        <div className="fleet-card p-4">
                            <h3 className="text-sm font-medium text-slate-700 mb-3">{el ? 'Σύνοψη σύγκρισης' : 'Comparison Summary'}</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <p className="text-zinc-500">{el ? 'Ημέρες διαφορά' : 'Days Between'}</p>
                                    <p className="text-slate-800 font-medium">
                                        {Math.abs(Math.floor((new Date(rightInspection.date) - new Date(leftInspection.date)) / (1000 * 60 * 60 * 24)))} {el ? 'ημέρες' : 'days'}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-zinc-500">{el ? 'Χιλιόμετρα' : 'Distance Traveled'}</p>
                                    <p className="text-[#1B7EB5] font-medium">
                                        {(rightInspection.odometer_reading - leftInspection.odometer_reading).toLocaleString()} km
                                    </p>
                                </div>
                                <div>
                                    <p className="text-zinc-500">{el ? 'Κατάσταση πριν' : 'Before Status'}</p>
                                    <p className={`font-medium ${leftInspection.status === 'approved' ? 'text-green-500' : leftInspection.status === 'flagged' ? 'text-red-500' : 'text-[#1B7EB5]'}`}>
                                        {leftInspection.status}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-zinc-500">{el ? 'Κατάσταση μετά' : 'After Status'}</p>
                                    <p className={`font-medium ${rightInspection.status === 'approved' ? 'text-green-500' : rightInspection.status === 'flagged' ? 'text-red-500' : 'text-[#1B7EB5]'}`}>
                                        {rightInspection.status}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
